import { handlePike13OAuthRedirect } from 'features/Migrate';
import { makePath } from 'helpers/link-generation';

import { createRoutes } from './routes.helpers';

import {
  redirectContactUs,
  redirectDynamicPricing,
  redirectEditClasses,
  redirectEditVenueDetails,
  redirectEditVenuePhotos,
  redirectHome,
  redirectPike13,
  redirectPrintRoster,
  redirectRatings,
  redirectRatingsReviews,
  redirectReports,
  redirectStats,
  redirectYieldManagement,
  redirectAccountSettings,
  redirectPathToPartnership,
  redirectWithVenueId,
  redirectWithMSIVenueId,
  redirect,
} from './redirect.saga';

const redirectRoutes = [
  // honor old bookmarks
  {
    path: '/manage/dynamic-pricing',
    pathSagas: [redirectDynamicPricing],
  },
  {
    path: '/venue-page/:venueId/classes/:classId?',
    pathSagas: [redirectEditClasses],
  },
  {
    path: '/venue-page/:venueId/photos',
    pathSagas: [redirectEditVenuePhotos],
  },
  {
    path: '/venue-page/:venueId',
    pathSagas: [redirectEditVenueDetails],
  },
  {
    path: '/venue-page/:venueId/*',
    pathSagas: [redirectEditVenueDetails],
  },
  {
    path: '/venue-page',
    exact: true,
    pathSagas: [redirectHome],
  },
  {
    path: '/classes/:venueId/daily',
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/classes/:venueId/weekly',
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/classes/:venueId/daily/*',
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/classes/:venueId/weekly/*',
    pathSagas: [redirectYieldManagement],
  },

  // old angular routes
  {
    path: '/help/contact-us',
    pathSagas: [redirectContactUs],
  },
  {
    path: '/onboarding/front-desk/success',
    pathSagas: [handlePike13OAuthRedirect],
  },
  {
    path: '/onboarding/front-desk/complete',
    pathSagas: [redirectHome],
  },
  {
    path: '/onboarding/front-desk/:venueId/:step?',
    pathSagas: [redirectPike13],
  },
  {
    path: '/onboarding/front-desk',
    pathSagas: [redirectHome],
  },
  {
    path: '/onboarding',
    exact: true,
    pathSagas: [redirectHome],
  },
  {
    path: '/manage/availability/:venueId?',
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/manage/availability',
    exact: true,
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/manage/schedule',
    exact: true,
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/manage',
    exact: true,
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/print/rosters/:scheduleId?/:venueId?/:venueName?',
    pathSagas: [redirectPrintRoster],
  },
  {
    path: '/engagement/:venueId?/ratings/:tab?',
    pathSagas: [redirectRatings],
  },
  {
    path: '/reports/rating/:venueId/overview',
    pathSagas: [redirectRatings],
  },
  {
    path: '/reports/rating/:venueId/reviews',
    pathSagas: [redirectRatingsReviews],
  },
  {
    path: '/reports/rating/:venueId/class/:classId?',
    pathSagas: [redirectRatings],
  },
  {
    path: '/reports/rating/:venueId/teacher/:instructorId',
    pathSagas: [redirectRatings],
  },
  {
    path: '/reports/rating/:venueId',
    pathSagas: [redirectRatings],
  },
  {
    path: '/rosters/:venueId/:mode?/:startDate?/edit?/:scheduleId?',
    pathSagas: [redirectYieldManagement],
  },
  {
    path: '/rosters',
    pathSagas: [redirectYieldManagement],
  },
  {
    // Report types can be classes, reservations, and summary
    path: '/reports/:reportType/:scheduleId',
    pathSagas: [redirectReports],
  },
  {
    path: '/reports/:reportType',
    pathSagas: [redirectReports],
  },
  {
    path: '/reports',
    pathSagas: [redirectReports],
  },
  {
    path: '/stats',
    exact: true,
    pathSagas: [redirectStats],
  },
  {
    path: '/reset',
    pathSagas: [redirectAccountSettings],
  },
  // Removed XPO route.
  {
    path: '/partner-signup/xpo',
    pathSagas: [redirectPathToPartnership],
    isPublic: true,
  },

  {
    path: '/path-to-partnership',
    pathSagas: [redirectPathToPartnership],
  },

  // Venue ID redirects
  ...createRoutes(['/venues', '/venues/profile', '/venues/edit/profile'], {
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.editVenue(venueId, 'profile'))],
  }),
  ...createRoutes(['/venues/auto-sync', '/venues/edit/auto-sync'], {
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.editVenue(venueId, 'auto-sync'))],
  }),

  ...createRoutes(['/venues/settings', '/venues/edit/settings'], {
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.editVenue(venueId, 'settings'))],
  }),
  {
    path: '/appointments/calendar-links',
    exact: true,
    pathSagas: [redirectWithMSIVenueId.bind(null, makePath.calendarLinks)],
  },
  {
    path: '/appointments/calendar',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.appointmentsCalendar({ venueId }))],
  },
  ...createRoutes(
    [
      '/engagement/promotions',
      '/engagement/campaigns',
      '/engagement/:venueId/promotions',
      '/engagement/:venueId/campaigns',
    ],
    {
      exact: true,
      pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.campaigns({ venueId }))],
    }
  ),
  {
    path: '/engagement/:venueId/campaigns/add',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.campaignsAdd({ venueId }))],
  },
  {
    path: '/engagement/:venueId/campaigns/:partnerCampaignId/edit',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.campaignsEdit({ venueId }))],
  },
  {
    path: '/engagement/:venueId/campaigns',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.campaigns({ venueId }))],
  },
  {
    path: '/classes/opportunities',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, makePath.opportunities)],
  },

  {
    path: '/manage/services',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.manageServices({ venueId }))],
  },
  {
    path: '/manage/practitioners',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.manageServices({ venueId, tab: 'practitioners' }))],
  },
  {
    path: '/manage/resources',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.manageServices({ venueId, tab: 'resources' }))],
  },
  {
    path: '/venues/classes',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, makePath.editClass)],
  },
  {
    path: '/stats/your-insights',
    exact: true,
    pathSagas: [redirectWithVenueId.bind(null, venueId => makePath.stats('your-insights', { venueId }))],
  },
];

// TODO(ralph): Convert the rest of the redirects to this format to cut down
// on the amount of individual redirect sagas
const redirectMap = [
  {
    from: '/stats/reports/promotions',
    to: makePath.reports,
    toArgs: [
      {
        reportType: 'campaigns',
      },
    ],
  },
];

redirectRoutes.push(
  ...redirectMap.map(({ from, to, toArgs }) => ({
    path: from,
    pathSagas: [redirect.bind(null, to(...toArgs))],
  }))
);

export default redirectRoutes;
